import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Site-wide scroll reveal.
 *
 * Content lifts gently into place the first time it scrolls into view. Once
 * revealed it stays revealed.
 *
 * The design point worth understanding:
 *
 *   An element is only ever hidden if it is BELOW THE FOLD at the moment we
 *   first see it. Anything already on screen is marked revealed immediately
 *   and never gets the hidden state at all.
 *
 * That single rule removes three problems at once:
 *
 *   1. No flash. Content in view is never hidden and then animated back in.
 *   2. No race with the network. Products arriving late are measured when they
 *      arrive, not against a timer that has already expired.
 *   3. No blank page. The hidden state only exists on elements this code has
 *      touched, so if the bundle fails to load or this file throws, nothing is
 *      hidden — there is no global "hide everything" class to get stuck on.
 *
 * The earlier version added a `js-reveal` class to <html> up front and used a
 * 1.2s timer to undo it if the observer never fired. On a slow page the timer
 * won the race and killed the reveal on exactly the pages that needed it.
 *
 * Everything animates transform and opacity only — no layout, no paint.
 */

// Existing class names across the site, so no page markup has to change.
//
// Every entry below was read out of the JSX that renders it — nothing here is
// guessed. The list deliberately targets CONTENT BLOCKS (a section, a card, a
// banner, a footer column) rather than page wrappers: a wrapper that contains
// the whole page would be measured once, at the top, and would therefore never
// be hidden, so it would animate nothing.
//
// Two kinds of element are deliberately left out:
//   · position: sticky panels (.cart-summary-new, .address-showing-summary) —
//     a transform on a sticky box changes what it is positioned against.
//   · anything that hosts a position: fixed descendant (modals, overlays),
//     for the same containing-block reason.
const SELECTOR = [
  // ── Home ────────────────────────────────────────────────────────────────
  ".home-section-label",       // Home.jsx: "Latest drop" / "Collections"
  ".home-shelf",               // Home.jsx: one shelf per drop
  ".home-product-card",        // Home.jsx: product tiles inside a shelf
  ".home-collection-banner",   // Home.jsx: each collection banner link

  // ── Product listing / cards ─────────────────────────────────────────────
  ".product-card-container",   // Sample.jsx + NewProducts.jsx grid items
  ".pg-empty",                 // NewProducts.jsx empty state

  // ── Product detail ──────────────────────────────────────────────────────
  ".pd-section",               // ProductDetails.jsx: size / colour / details
  ".pd-block",                 // ProductDetails.jsx: wash care, shipping
  ".pd-related",               // ProductDetails.jsx: related products
  ".pd-reviews",               // ProductDetails.jsx: ratings & reviews

  // ── Reviews ─────────────────────────────────────────────────────────────
  ".reviews-header",           // Reviews.jsx
  ".reviews-item",             // Reviews.jsx: one review per row

  // ── About + Contact ─────────────────────────────────────────────────────
  ".about-logo-row",           // About.jsx
  ".about-section",            // About.jsx and ContactUs.jsx both use this
  ".about-value-item",         // About.jsx: Design / Craft / Identity
  ".values-list",              // ContactUs.jsx
  ".about-social",             // ContactUs.jsx

  // ── Policy / legal / shipping copy ──────────────────────────────────────
  ".website-banner",           // Policy.jsx header banner
  ".policy-section",           // Policy.jsx
  ".cta-section",              // Policy.jsx closing call to action
  ".image-container",          // ShippingPolicy / Privacy / Terms / Refunds
  ".shipping-text",            // ShippingPolicy / Privacy / Terms / Refunds

  // ── Size guide ──────────────────────────────────────────────────────────
  ".size-guide-title",         // SizeGuid.jsx
  ".size-guide-section",       // SizeGuid.jsx

  // ── Cart / checkout / account ───────────────────────────────────────────
  ".cart-title-new",           // CartPage.jsx
  ".cart-item-new",            // CartPage.jsx: one line item per row
  ".delivery-options",         // CartPage.jsx
  ".address-card",             // Shipping.jsx: saved addresses
  ".order-history-card",       // OrderHistory.jsx
  ".tab-content",              // Profile.jsx: account panels
  ".account-form",             // Profile.jsx

  // ── Footer (rendered on nearly every page, always below the fold) ────────
  ".footer-logo-section",
  ".footer-subscribe-section",
  ".timer-container",
  ".footer-bottom",
  ".footer-footer",

  // ── Opt-in escape hatch for any future markup ───────────────────────────
  "[data-reveal]",
].join(",");

// Stagger is applied at REVEAL time, not at measure time: whatever crosses the
// line together cascades together. Measuring-time indices produced arbitrary
// delays, because two elements at opposite ends of a long page would be given
// neighbouring numbers and then reveal minutes apart.
const STAGGER_STEP = 90; // ms between neighbours
const STAGGER_MAX = 5;   // caps the tail at 450ms so nothing waits long

const HIDDEN = "reveal";
const SHOWN = "is-in";
const SEEN = "data-reveal-seen";

export default function useScrollReveal() {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!("IntersectionObserver" in window)) return undefined;

    // Reduced motion: nothing hidden, nothing observed, no work done.
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }

    // Reveals a batch together, top-to-bottom, each one a beat behind the last.
    // The delay is a CSS transition-delay, never a timer: the class is added
    // synchronously here, so nothing depends on a callback firing later to
    // become visible.
    const showBatch = (els) => {
      if (!els.length) return;
      if (els.length > 1) {
        els.sort((a, b) => {
          const ra = a.getBoundingClientRect();
          const rb = b.getBoundingClientRect();
          return ra.top - rb.top || ra.left - rb.left;
        });
      }
      els.forEach((el, i) => {
        const step = Math.min(i, STAGGER_MAX) * STAGGER_STEP;
        if (step) el.style.setProperty("--reveal-delay", `${step}ms`);
        el.classList.add(SHOWN);
        observer.unobserve(el);
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        showBatch(entries.filter((e) => e.isIntersecting).map((e) => e.target));
      },
      { threshold: 0.1, rootMargin: "0px 0px -8% 0px" }
    );

    const collect = () => {
      const viewportH = window.innerHeight || document.documentElement.clientHeight;

      document.querySelectorAll(SELECTOR).forEach((el) => {
        if (el.hasAttribute(SEEN)) return;
        el.setAttribute(SEEN, "");

        const top = el.getBoundingClientRect().top;

        // Already on screen, or scrolled past. Never hide it.
        if (top < viewportH * 0.92) {
          el.classList.add(HIDDEN, SHOWN);
          return;
        }

        // Below the fold: safe to hide, then reveal on approach.
        el.classList.add(HIDDEN);
        observer.observe(el);
      });
    };

    // Belt and braces. This does on scroll exactly what the observer does, so
    // IntersectionObserver is an optimisation rather than a dependency. In a
    // throttled or backgrounded tab the observer can simply never deliver a
    // callback; without this, everything below the fold would stay hidden no
    // matter how far the visitor scrolled.
    const sweep = () => {
      const viewportH = window.innerHeight || document.documentElement.clientHeight;
      const due = [];
      document.querySelectorAll("." + HIDDEN + ":not(." + SHOWN + ")").forEach((el) => {
        if (el.getBoundingClientRect().top < viewportH * 0.92) due.push(el);
      });
      showBatch(due);
    };

    const onScroll = () => {
      collect();
      sweep();
    };

    collect();

    // Products and orders are fetched after mount, so watch for them arriving.
    const mo = new MutationObserver(onScroll);
    mo.observe(document.body, { childList: true, subtree: true });

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      mo.disconnect();
      observer.disconnect();
    };
  }, [pathname]);
}
